/**
 * Appels Amazon Cognito (sans SDK) : code par e-mail (USER_AUTH / EMAIL_OTP – aucun mot de passe),
 * SSO Entra ID fédéré (autorisation OAuth 2.0 + PKCE), rafraîchissement, déconnexion.
 */
import type { ConfigApp } from '../config';
import type { Jetons, OrigineJetons } from './session';

/**
 * Erreur de connexion : `code` = clé connexion.erreurs.<code> du dictionnaire. `transitoire` : Cognito injoignable
 * (réseau coupé), indisponible (5xx) ou trop sollicité – un nouvel essai peut réussir, le jeton n'est pas refusé.
 */
export class ErreurConnexion extends Error {
  constructor(
    public code: 'codeInvalide' | 'compteInconnu' | 'compteLie' | 'nonAutorise' | 'trop' | 'generique',
    message?: string,
    public transitoire = false,
  ) {
    super(message || code);
  }
}

/** Échec passager (réseau, Cognito indisponible) : la session n'est pas perdue. */
export const estTransitoire = (e: unknown): boolean => e instanceof ErreurConnexion && e.transitoire;

interface ReponseAuth {
  AuthenticationResult?: { IdToken: string; AccessToken: string; RefreshToken?: string; ExpiresIn: number };
  ChallengeName?: string;
  Session?: string;
}

function erreurDepuisType(type: string): ErreurConnexion {
  const t = type.split('#').pop() || type;
  if (t === 'NotAuthorizedException') return new ErreurConnexion('codeInvalide', t);
  if (t === 'CodeMismatchException' || t === 'ExpiredCodeException') return new ErreurConnexion('codeInvalide', t);
  if (t === 'UserNotFoundException') return new ErreurConnexion('compteInconnu', t);
  if (t === 'TooManyRequestsException' || t === 'LimitExceededException' || t === 'TooManyFailedAttemptsException') return new ErreurConnexion('trop', t);
  return new ErreurConnexion('generique', t);
}

async function appel(cfg: ConfigApp, operation: string, corps: unknown): Promise<ReponseAuth> {
  let r: Response;
  try {
    r = await fetch(`https://cognito-idp.${cfg.region}.amazonaws.com/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}` },
      body: JSON.stringify(corps),
    });
  } catch {
    throw new ErreurConnexion('generique', 'reseau', true);
  }
  const json = (await r.json().catch(() => ({}))) as ReponseAuth & { __type?: string; message?: string };
  if (!r.ok) {
    const e = erreurDepuisType(json.__type || '');
    e.transitoire = r.status >= 500 || e.code === 'trop';
    throw e;
  }
  return json;
}

function versJetons(res: ReponseAuth, origine: OrigineJetons, refreshPrecedent?: string): Jetons {
  const a = res.AuthenticationResult;
  if (!a) throw new ErreurConnexion('generique', res.ChallengeName || 'sans-jetons');
  return { idToken: a.IdToken, accessToken: a.AccessToken, refreshToken: a.RefreshToken || refreshPrecedent, expireA: Date.now() + a.ExpiresIn * 1000, origine };
}

/** Code par e-mail, étape 1 : Cognito envoie un code ; renvoie la session du défi. */
export async function demanderCode(cfg: ConfigApp, email: string): Promise<string> {
  const res = await appel(cfg, 'InitiateAuth', {
    ClientId: cfg.cognito.clientId, AuthFlow: 'USER_AUTH', AuthParameters: { USERNAME: email.trim().toLowerCase(), PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  });
  if (res.ChallengeName !== 'EMAIL_OTP' || !res.Session) throw new ErreurConnexion('generique', res.ChallengeName || 'defi-inattendu');
  return res.Session;
}

/** Code par e-mail, étape 2 : vérification du code. */
export async function validerCode(cfg: ConfigApp, email: string, session: string, code: string): Promise<{ jetons?: Jetons; session?: string }> {
  const res = await appel(cfg, 'RespondToAuthChallenge', {
    ClientId: cfg.cognito.clientId, ChallengeName: 'EMAIL_OTP', Session: session,
    ChallengeResponses: { USERNAME: email.trim().toLowerCase(), EMAIL_OTP_CODE: code.trim() },
  });
  if (res.AuthenticationResult) return { jetons: versJetons(res, 'code') };
  if (res.ChallengeName === 'EMAIL_OTP' && res.Session) return { session: res.Session };   // nouvel essai autorisé
  throw new ErreurConnexion('generique', res.ChallengeName || 'defi-inattendu');
}

/* ---------------------------------------------------------------- SSO (OAuth 2.0 + PKCE) */

const CLE_PKCE = 'agora.pkce';

function aleatoire(octets: number): string {
  const t = new Uint8Array(octets);
  crypto.getRandomValues(t);
  return base64url(t);
}

function base64url(t: Uint8Array): string {
  let s = '';
  t.forEach((o) => { s += String.fromCharCode(o); });
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const urlRetourSso = () => `${window.location.origin}/auth/retour`;

/** Redirige vers Entra ID via Cognito (identity_provider = fournisseur fédéré). */
export async function lancerSso(cfg: ConfigApp): Promise<void> {
  const verificateur = aleatoire(48);
  const etat = aleatoire(16);
  const defi = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verificateur))));
  sessionStorage.setItem(CLE_PKCE, JSON.stringify({ verificateur, etat }));
  const q = new URLSearchParams({
    identity_provider: cfg.cognito.fournisseurSso, response_type: 'code', client_id: cfg.cognito.clientId, redirect_uri: urlRetourSso(),
    scope: 'openid email profile', state: etat, code_challenge: defi, code_challenge_method: 'S256',
  });
  window.location.assign(`https://${cfg.cognito.domaine}/oauth2/authorize?${q.toString()}`);
}

async function jetonOauth(cfg: ConfigApp, params: Record<string, string>): Promise<ReponseAuth['AuthenticationResult'] & { refresh_token?: string }> {
  let r: Response;
  try {
    r = await fetch(`https://${cfg.cognito.domaine}/oauth2/token`, {
      method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams(params).toString(),
    });
  } catch {
    throw new ErreurConnexion('generique', 'reseau', true);
  }
  const j = (await r.json().catch(() => ({}))) as { id_token?: string; access_token?: string; refresh_token?: string; expires_in?: number; error?: string };
  // invalid_grant (jeton expiré ou révoqué) et les autres 4xx sont définitifs ; 429 et 5xx sont passagers.
  if (!r.ok || !j.id_token || !j.access_token) throw new ErreurConnexion('generique', j.error || `oauth ${r.status}`, r.status === 429 || r.status >= 500);
  return { IdToken: j.id_token, AccessToken: j.access_token, RefreshToken: j.refresh_token, ExpiresIn: j.expires_in || 3600 };
}

/** Retour du SSO (/auth/retour) : contrôle de l'état puis échange du code contre les jetons. */
export async function terminerSso(cfg: ConfigApp, params: URLSearchParams): Promise<Jetons> {
  const erreur = params.get('error_description') || params.get('error');
  if (erreur) {
    if (/COMPTE_LIE/.test(erreur)) throw new ErreurConnexion('compteLie', erreur);
    if (/COMPTE_INCONNU/.test(erreur)) throw new ErreurConnexion('compteInconnu', erreur);
    if (/NON_AUTORISE|access_denied/i.test(erreur)) throw new ErreurConnexion('nonAutorise', erreur);
    throw new ErreurConnexion('generique', erreur);
  }
  const brut = sessionStorage.getItem(CLE_PKCE);
  sessionStorage.removeItem(CLE_PKCE);
  const pkce = brut ? (JSON.parse(brut) as { verificateur: string; etat: string }) : null;
  const code = params.get('code');
  if (!pkce || !code || params.get('state') !== pkce.etat) throw new ErreurConnexion('generique', 'etat-invalide');
  const a = await jetonOauth(cfg, { grant_type: 'authorization_code', client_id: cfg.cognito.clientId, code, redirect_uri: urlRetourSso(), code_verifier: pkce.verificateur });
  return versJetons({ AuthenticationResult: a }, 'sso');
}

/** Rafraîchit les jetons (le refresh token reste le même). */
export async function rafraichirJetons(cfg: ConfigApp, refreshToken: string, origine: OrigineJetons): Promise<Jetons> {
  if (origine === 'sso') {
    const a = await jetonOauth(cfg, { grant_type: 'refresh_token', client_id: cfg.cognito.clientId, refresh_token: refreshToken });
    return versJetons({ AuthenticationResult: a }, 'sso', refreshToken);
  }
  const res = await appel(cfg, 'InitiateAuth', { ClientId: cfg.cognito.clientId, AuthFlow: 'REFRESH_TOKEN_AUTH', AuthParameters: { REFRESH_TOKEN: refreshToken } });
  return versJetons(res, origine, refreshToken);
}

/** Révoque le refresh token (best effort) et, après un SSO, ferme la session Cognito. */
export async function deconnexionDistante(cfg: ConfigApp, jetons: Jetons | null): Promise<void> {
  if (jetons?.refreshToken) {
    await fetch(`https://cognito-idp.${cfg.region}.amazonaws.com/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': 'AWSCognitoIdentityProviderService.RevokeToken' },
      body: JSON.stringify({ ClientId: cfg.cognito.clientId, Token: jetons.refreshToken }),
    }).catch(() => undefined);
  }
  if (jetons?.origine === 'sso') {
    const q = new URLSearchParams({ client_id: cfg.cognito.clientId, logout_uri: `${window.location.origin}/connexion` });
    window.location.assign(`https://${cfg.cognito.domaine}/logout?${q.toString()}`);
  }
}
